import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useKillAgent } from '@/hooks/agents/useAgents';
import { SKIP_AUTO_SELECT_STATE } from '@/lib/agents-index-route-state';
import { useOrgRoutes } from '@/lib/useOrgRoutes';

/**
 * Stops viewing an agent that has just been deleted.
 *
 * The URL is the source of truth for which run is open, so this only has to
 * ask whether the deleted set contains the id in the path — no surface has to
 * thread its own selection state through the delete flow. The navigation is a
 * `replace` (the entry it leaves behind points at a run that no longer exists)
 * and carries {@link SKIP_AUTO_SELECT_STATE} so the index lands on its
 * no-agent-selected screen rather than opening the next run in the list.
 *
 * Surfaces with no agent id in the path (the inbox, for example) never
 * navigate: nothing was being viewed, so nothing needs to stop.
 */
export function useStopViewingDeletedAgents(): (deletedIds: readonly string[]) => void {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const routes = useOrgRoutes();

  return useCallback(
    (deletedIds: readonly string[]) => {
      if (!agentId || !deletedIds.includes(agentId)) return;
      navigate(routes.runs, { replace: true, state: SKIP_AUTO_SELECT_STATE });
    },
    [agentId, navigate, routes],
  );
}

/**
 * Deletes one agent and leaves its view if it was the open one.
 *
 * Every single-agent delete goes through {@link DeleteAgentDialog}, which
 * calls this — so "deleting the agent you are looking at closes it" is one
 * rule in one place instead of a per-surface callback. The delete fires
 * immediately rather than after the request settles: the confirm dialog
 * unmounts on confirm, and `useKillAgent` owns the result toasts.
 */
export function useDeleteAgent(): (agentId: string) => void {
  const kill = useKillAgent();
  const killMutate = kill.mutate;
  const stopViewingDeleted = useStopViewingDeletedAgents();

  return useCallback(
    (agentId: string) => {
      killMutate(agentId);
      stopViewingDeleted([agentId]);
    },
    [killMutate, stopViewingDeleted],
  );
}
